import { useState } from "react";
import { toast } from "sonner";
import { Instagram, Mail, MessageCircle, BadgeCheck, Loader2, Send, Linkedin, Clock } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { SITE, waLink } from "../config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Child Development & Play Therapy",
  "Anxiety & Emotional Regulation",
  "Stress & Coping",
  "Adult Issues & Self-Growth",
  "Family Conflicts",
  "Work Stress & Burnout",
  "Not sure yet — help me decide",
];

const initial = { name: "", email: "", phone: "", service: SERVICES[0], mode: "Online", message: "" };

const inputCls =
  "w-full rounded-2xl border border-[#064E3B]/15 bg-[#FFFDF9] px-4 py-3 text-sm text-[#1C2826] outline-none transition-colors duration-300 placeholder:text-[#526560]/50 focus:border-[#064E3B] focus:ring-2 focus:ring-[#064E3B]/15";

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      toast.success("Thank you — your enquiry is on its way. I usually reply within 24 hours.");
      setForm(initial);
    } catch {
      toast.error("Something went wrong sending your enquiry. Please try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="grain bg-[#FDFBF7] py-20 lg:py-28">
      <div className="relative z-[2] mx-auto grid max-w-7xl grid-cols-1 gap-14 px-4 sm:px-8 lg:grid-cols-12 lg:gap-20 lg:px-16">
        <Reveal className="lg:col-span-5">
          <Eyebrow>Begin here</Eyebrow>
          <h2 className="mt-4 font-serif text-3xl font-medium leading-snug tracking-tight text-[#064E3B] sm:text-4xl lg:text-5xl">
            A gentle first step is <em className="text-[#059669]">enough</em>
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-[#526560]">
            Tell me a little about what brings you here. Every enquiry is read personally and
            answered within 24 hours — your words stay confidential, always.
          </p>
          <div className="mt-9 space-y-4">
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-link"
              className="flex items-center gap-3 text-sm font-medium text-[#1C2826] transition-colors hover:text-[#064E3B]"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#ECFDF5] text-[#064E3B]">
                <MessageCircle size={18} />
              </span>
              Chat on WhatsApp — fastest reply
            </a>
            <a
              href={`mailto:${SITE.email}`}
              data-testid="contact-email-link"
              className="flex items-center gap-3 text-sm font-medium text-[#1C2826] transition-colors hover:text-[#064E3B]"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#ECFDF5] text-[#064E3B]">
                <Mail size={18} />
              </span>
              {SITE.email}
            </a>
            <a
              href={SITE.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-instagram-link"
              className="flex items-center gap-3 text-sm font-medium text-[#1C2826] transition-colors hover:text-[#064E3B]"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#ECFDF5] text-[#064E3B]">
                <Instagram size={18} />
              </span>
              {SITE.instagramHandle}
            </a>
            <a
              href={SITE.mantraCareUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-mantra-care-link"
              className="flex items-center gap-3 text-sm font-medium text-[#1C2826] transition-colors hover:text-[#064E3B]"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#ECFDF5] text-[#064E3B]">
                <BadgeCheck size={18} />
              </span>
              Verified psychologist at Mantra Care
            </a>
            <a
              href={SITE.linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-linkedin-link"
              className="flex items-center gap-3 text-sm font-medium text-[#1C2826] transition-colors hover:text-[#064E3B]"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#ECFDF5] text-[#064E3B]">
                <Linkedin size={18} />
              </span>
              Connect on LinkedIn
            </a>
            <p className="flex items-center gap-3 text-sm font-medium text-[#1C2826]">
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#ECFDF5] text-[#064E3B]">
                <Clock size={18} />
              </span>
              <span>
                Session hours
                <span className="block text-xs font-normal text-[#526560]">{SITE.availability}</span>
              </span>
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.12} className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="contact-booking-form"
            className="rounded-[2rem] border border-[#064E3B]/12 bg-[#F7F4EB] p-6 shadow-[0_24px_60px_-35px_rgba(6,78,59,0.45)] sm:p-9"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-[#064E3B]">
                  Your name *
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-name-input"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Parent / client name"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-[#064E3B]">
                  Email *
                </label>
                <input
                  id="contact-email"
                  data-testid="contact-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@example.com"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-phone" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-[#064E3B]">
                  Phone / WhatsApp
                </label>
                <input
                  id="contact-phone"
                  data-testid="contact-phone-input"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="+91 ..."
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-service" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-[#064E3B]">
                  I&rsquo;m enquiring about
                </label>
                <select
                  id="contact-service"
                  data-testid="contact-service-select"
                  value={form.service}
                  onChange={set("service")}
                  className={inputCls}
                >
                  {SERVICES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-5">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-[#064E3B]">
                Preferred mode
              </span>
              <div className="flex gap-3" role="radiogroup" aria-label="Preferred session mode">
                {["Online", "In-person"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    data-testid={`contact-mode-${m.toLowerCase().replace("-", "")}`}
                    onClick={() => setForm((f) => ({ ...f, mode: m }))}
                    aria-pressed={form.mode === m}
                    className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                      form.mode === m
                        ? "bg-[#064E3B] text-[#FDFBF7]"
                        : "border border-[#064E3B]/20 bg-[#FFFDF9] text-[#064E3B] hover:bg-[#ECFDF5]"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-5">
              <label htmlFor="contact-message" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-[#064E3B]">
                A few words about what brings you here *
              </label>
              <textarea
                id="contact-message"
                data-testid="contact-message-input"
                required
                minLength={5}
                rows={4}
                value={form.message}
                onChange={set("message")}
                placeholder="Share as much or as little as you like — this stays between us."
                className={`${inputCls} resize-none`}
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-form-submit-button"
              className="group mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-[#064E3B] px-7 py-4 text-sm font-semibold text-[#FDFBF7] transition-all duration-300 hover:bg-[#022C22] hover:shadow-[0_14px_36px_-12px_rgba(6,78,59,0.55)] disabled:opacity-60 sm:w-auto"
            >
              {sending ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Sending...
                </>
              ) : (
                <>
                  Send my enquiry
                  <Send size={15} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                </>
              )}
            </button>
            <p className="mt-4 text-xs text-[#526560]">
              Confidential &amp; judgement-free. Sessions start at {SITE.price} — the first 15-minute
              discovery call is free.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
